import { useEffect, useState } from "react";
import TextInput from "../../component/textInput/TextInput";
import { RequiredLabel } from "../../component/requiredLabel/RequiredLabel";
import "./Vehicles.css";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../component/buttonComponent/ButtonComponent";
import { ToggleSwitch } from "../../component/toggle/ToggleSwitch";
import { APICheckVehicleTelematics } from "../../config/utilities/APIs/vehiclesApi";

export const VehicleForm = ({
  mode,
  existingData,
  onSubmit,
  handleReset,
  loading,
  setLoading,
  input,
  setInput,
  error,
  setError,
  token,
  institutionId,
  onTelematicsCheckFeedback,
}) => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [initialInput, setInitialInput] = useState(null);
  const [keyUp, setKeyUp] = useState("", false);
  const [telematicsError, setTelematicsError] = useState("");
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  const isVehicleFormValid = () => {
    let isValid = true;

    const noSpecialChars = /^[a-zA-Z0-9 ]*$/;

    const newErrors = {
      busNumber: input.busNumber ? input.busNumber.length > 100 : false,
      vehicleNumber:
        input.vehicleNumber === "" ||
        input.vehicleNumber.length > 60 ||
        !noSpecialChars.test(input.vehicleNumber),
      brand:
        input.brand === "" ||
        input.brand.length > 60 ||
        !noSpecialChars.test(input.brand),
      model:
        input.model === "" ||
        input.model.length > 60 ||
        !noSpecialChars.test(input.model),
      color:
        input.color === "" ||
        input.color.length > 60 ||
        !noSpecialChars.test(input.color),
      capacity: input.capacity === "" || isNaN(Number(input.capacity)),
    };

    isValid = Object.values(newErrors).every((v) => !v);

    const normalizeInput = (obj) => ({
      busNumber: obj.busNumber?.trim?.() ?? "",
      vehicleNumber: obj.vehicleNumber.trim(),
      brand: obj.brand.trim(),
      model: obj.model.trim(),
      color: obj.color.trim(),
      capacity: obj.capacity.trim?.() ?? "",
      status: obj.status,
      useTelematic: obj.useTelematic === true,
    });

    const isEdited = initialInput
      ? JSON.stringify(normalizeInput(initialInput)) !==
        JSON.stringify(normalizeInput(input))
      : Object.values(normalizeInput(input)).some((val) => val !== "");

    return isValid && isEdited;
  };

  useEffect(() => {
    const noSpecialChars = /^[a-zA-Z0-9 ]*$/;

    const getFieldError = (value, fieldName) => {
      if (value.length > 60)
        return `${fieldName} should not exceed 60 characters`;
      if (!noSpecialChars.test(value))
        return `${fieldName} contains invalid characters`;
      return "";
    };

    setError({
      busNumber:
        input.busNumber && input.busNumber.length > 100
          ? "Bus Number should not exceed 100 characters"
          : "",
      vehicleNumber:
        input.vehicleNumber &&
        getFieldError(input.vehicleNumber, "Registration Number"),
      brand: input.brand && getFieldError(input.brand, "Brand"),
      model: input.model && getFieldError(input.model, "Model"),
      color: input.color && getFieldError(input.color, "Color"),
      capacity:
        input.capacity && isNaN(Number(input.capacity))
          ? "Capacity must be a number"
          : "",
    });
  }, [input]);

  useEffect(() => {
    setIsSubmitDisabled(!isVehicleFormValid());
  }, [input, initialInput]);

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const busInputWidth = isMobileViewport ? "100%" : "45rem";
  const compactInputWidth = isMobileViewport ? "100%" : "20rem";
  const telematicsControlWidth = isMobileViewport ? "100%" : "16rem";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedInput = { ...input, [name]: value };
    setInput(updatedInput);
  };

  const isVehicleNumberValidForCheck = () => {
    const trimmed = input.vehicleNumber?.trim?.() || "";
    const noSpecialChars = /^[a-zA-Z0-9 ]*$/;
    return (
      trimmed.length > 0 && trimmed.length <= 60 && noSpecialChars.test(trimmed)
    );
  };

  const handleTelematicsToggle = (checked) => {
    if (mode === "view") return;
    setInput((prev) => ({ ...prev, useTelematic: checked === true }));
  };

  const handleCheckTelematics = async () => {
    if (!isVehicleNumberValidForCheck()) {
      const errorMessage = "Enter a valid registration number before checking.";
      setTelematicsError(errorMessage);
      onTelematicsCheckFeedback?.({
        type: "error",
        title: "Telematics Check Failed",
        subtitle: errorMessage,
      });
      return;
    }

    setTelematicsError("");
    const response = await APICheckVehicleTelematics(
      input.vehicleNumber,
      setLoading,
      institutionId,
      token
    );

    const telematicsData = response?.data?.data;
    if (response?.status === 200 && telematicsData) {
      const hasTelematics = telematicsData.isTelematic === true;
      setInput((prev) => ({
        ...prev,
        isTelematic: hasTelematics,
        useTelematic: hasTelematics ? prev.useTelematic === true : false,
      }));
      onTelematicsCheckFeedback?.({
        type: "success",
        title: "Telematics Check Complete",
        subtitle: hasTelematics
          ? "This vehicle has telematics."
          : "This vehicle does not have telematics.",
      });
      return;
    }

    const errorMessage =
      response?.response?.data?.message ||
      "Unable to check telematics right now. Please try again.";
    setTelematicsError(errorMessage);
    onTelematicsCheckFeedback?.({
      type: "error",
      title: "Telematics Check Failed",
      subtitle: errorMessage,
    });
  };

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && existingData) {
      const { _id, institutionId, capacity, status, ...rest } = existingData;

      const updatedFormattedData = {
        ...rest,
        capacity: capacity?.toString() ?? "",
        status,
        isTelematic: existingData?.isTelematic ?? null,
        useTelematic: existingData?.useTelematic === true,
      };

      setInitialInput(updatedFormattedData);

      setInput((prev) => ({
        ...prev,
        ...(existingData.busNumber !== undefined && {
          busNumber: existingData.busNumber,
        }),
        vehicleNumber: existingData.vehicleNumber || prev.vehicleNumber || "",
        brand: existingData.brand || prev.brand || "",
        model: existingData.model || prev.model || "",
        color: existingData.color || prev.color || "",
        capacity: (existingData.capacity ?? prev.capacity ?? "").toString(),
        isTelematic: existingData?.isTelematic ?? null,
        useTelematic: existingData?.useTelematic === true,
      }));
    } else {
      setInitialInput(input);
    }
  }, [mode, existingData]);

  return (
    <>
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Bus Number</RequiredLabel>
        </div>
        <div className="input-container">
          <TextInput
            placeholder={mode === "view" ? input.busNumber : "Enter Bus Number"}
            setInput={setInput}
            input={mode === "view" ? "" : input}
            name="busNumber"
            disable={mode === "view"}
            type="text"
            error={Boolean(error.busNumber)}
            setError={setError}
            errorMessage={error.busNumber}
            keyUp={keyUp}
            setKeyUp={setKeyUp}
            onChangeFunction={handleInputChange}
            width={busInputWidth}
            showCharCount
            maxLength={100}
          />
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Registration Number</RequiredLabel>
        </div>
        <div className="input-container">
          <TextInput
            placeholder={
              mode === "view" || mode === "edit"
                ? input.vehicleNumber
                : "Enter Registration Number"
            }
            setInput={setInput}
            input={mode === "view" || mode === "edit" ? "" : input}
            name="vehicleNumber"
            disable={mode === "view" || mode === "edit"}
            type="text"
            error={Boolean(error.vehicleNumber)}
            setError={setError}
            errorMessage={error.vehicleNumber}
            keyUp={keyUp}
            setKeyUp={setKeyUp}
            onChangeFunction={handleInputChange}
            width={busInputWidth}
            showCharCount
            maxLength={60}
          />
        </div>
      </div>
      <div className="vehicle-form-container">
        <div className="input-label">
          <RequiredLabel>Vehicle Details</RequiredLabel>
        </div>
        <div className="input-containers">
          <div className="input-container">
            <TextInput
              label="Brand"
              placeholder={mode === "view" ? input.brand : "Enter Brand"}
              setInput={setInput}
              input={mode === "view" ? "" : input}
              name="brand"
              disable={mode === "view"}
              type="text"
              error={Boolean(error.brand)}
              setError={setError}
              errorMessage={error.brand}
              keyUp={keyUp}
              setKeyUp={setKeyUp}
              onChangeFunction={handleInputChange}
              width={compactInputWidth}
              showCharCount
              maxLength={60}
            />
          </div>
          <div className="input-container">
            <TextInput
              label="Model"
              placeholder={mode === "view" ? input.model : "Enter Model"}
              setInput={setInput}
              input={mode === "view" ? "" : input}
              name="model"
              disable={mode === "view"}
              type="text"
              error={Boolean(error.model)}
              setError={setError}
              errorMessage={error.model}
              keyUp={keyUp}
              setKeyUp={setKeyUp}
              onChangeFunction={handleInputChange}
              width={compactInputWidth}
              showCharCount
              maxLength={60}
            />
          </div>
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label"></div>
        <div className="input-containers">
          <div className="input-container">
            <TextInput
              label="Color"
              placeholder={mode === "view" ? input.color : "Enter Color"}
              setInput={setInput}
              input={mode === "view" ? "" : input}
              name="color"
              disable={mode === "view"}
              type="text"
              error={Boolean(error.color)}
              setError={setError}
              errorMessage={error.color}
              keyUp={keyUp}
              setKeyUp={setKeyUp}
              onChangeFunction={handleInputChange}
              width={compactInputWidth}
              showCharCount
              maxLength={60}
            />
          </div>
          <div className="input-container">
            <TextInput
              label="Capacity"
              placeholder={mode === "view" ? input.capacity : "Enter Capacity"}
              setInput={setInput}
              input={mode === "view" ? "" : input}
              name="capacity"
              disable={mode === "view"}
              type="text"
              error={Boolean(error.capacity)}
              setError={setError}
              errorMessage={error.capacity}
              keyUp={keyUp}
              setKeyUp={setKeyUp}
              onChangeFunction={handleInputChange}
              width={compactInputWidth}
            />
          </div>
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">Telematics</div>
        <div className="input-containers telematics-controls-row">
          <div className="input-container telematics-toggle-container">
            <div
              className="telematics-toggle-wrapper"
              title={
                input.isTelematic === false
                  ? "This vehicle does not have telematics"
                  : ""
              }
            >
              <span className="telematics-toggle-label">Enable Telematics</span>
              <ToggleSwitch
                checked={input.useTelematic === true}
                disabled={mode === "view" || input.isTelematic !== true}
                onChange={handleTelematicsToggle}
                ariaLabel="Enable or disable telematics for vehicle"
              />
            </div>
          </div>
          {mode !== "view" && (
            <div className="input-container telematics-check-container">
              <SecondaryButton
                width={telematicsControlWidth}
                label={loading ? "Checking..." : "Check Telematics"}
                imageIcon={false}
                onClickFunction={handleCheckTelematics}
                disable={loading || !isVehicleNumberValidForCheck()}
              />
            </div>
          )}
          {telematicsError && (
            <p className="telematics-inline-error">{telematicsError}</p>
          )}
        </div>
      </div>
      <div className="button-container-edit">
        <SecondaryButton
          width="6rem"
          label="Cancel"
          imageIcon={false}
          onClickFunction={handleReset}
        />
        {mode !== "view" && (
          <PrimaryButton
            width="6rem"
            disable={isSubmitDisabled}
            label={mode === "create" ? "Create" : "Save"}
            onClickFunction={onSubmit}
            loading={loading}
          />
        )}
      </div>
    </>
  );
};
